"use client";

import { ArrowRight, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
export default function Services() {
  const cover = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const collections = [{
    index: "A",
    eyebrow: "§ A — REVENUE",
    titleStart: "OTA Revenue",
    titleItalic: "Management",
    titleEnd: "Strategies",
    description: "Maximize your property's visibility and conversion rates through data-driven OTA listing optimization and dynamic rate management.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp",
    imageAlt: "OTA revenue management dashboard showing growth metrics",
    ghostEdge: "left",
    dishes: [{
      name: "OTA Listing Audit",
      notes: "Comprehensive review of content, images, and ranking factors",
      tags: "SEO"
    }, {
      name: "Dynamic Rate Parity",
      notes: "Real-time monitoring to ensure consistent pricing across channels",
      tags: "TECH"
    }, {
      name: "Conversion Optimization",
      notes: "Improving booking engine flow and guest trust signals",
      tags: "GROWTH"
    }, {
      name: "Channel Manager Setup",
      notes: "Seamless integration of distribution platforms",
      tags: "TECH"
    }, {
      name: "Competitor Benchmarking",
      notes: "Strategic analysis of local market pricing trends",
      tags: "DATA"
    }]
  }, {
    index: "B",
    eyebrow: "§ B — TECHNOLOGY",
    titleStart: "Hospitality Tech",
    titleItalic: "Implementation",
    titleEnd: "",
    description: "Modernize your hotel's digital infrastructure with expert recommendations and seamless integration of industry-leading software stacks.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png",
    imageAlt: "Modern hotel technology stack interface",
    ghostEdge: "right",
    dishes: [{
      name: "PMS Selection",
      notes: "Finding the right Property Management System for your scale",
      tags: "TECH"
    }, {
      name: "Booking Engine Integration",
      notes: "High-converting direct booking solutions",
      tags: "GROWTH"
    }, {
      name: "Revenue Intelligence",
      notes: "Advanced forecasting and demand prediction tools",
      tags: "DATA"
    }, {
      name: "Digital Marketing Sync",
      notes: "Connecting your website to revenue management systems",
      tags: "SEO"
    }, {
      name: "System Migration",
      notes: "Safe and efficient transition between software platforms",
      tags: "TECH"
    }]
  }, {
    index: "C",
    eyebrow: "§ C — AUDITS",
    titleStart: "Revenue",
    titleItalic: "Performance",
    titleEnd: "Audits",
    description: "A deep-dive analysis of your current revenue streams, identifying hidden opportunities for growth and operational efficiency.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_performance_audit.webp",
    imageAlt: "Professional revenue audit report on a desk",
    ghostEdge: "left",
    dishes: [{
      name: "Full Revenue Audit",
      notes: "Complete assessment of your current revenue strategy",
      tags: "DATA"
    }, {
      name: "Direct Booking Audit",
      notes: "Identifying barriers to direct website reservations",
      tags: "GROWTH"
    }, {
      name: "Distribution Audit",
      notes: "Evaluating channel mix and commission costs",
      tags: "DATA"
    }, {
      name: "Pricing Strategy Review",
      notes: "Optimizing rate plans and seasonal adjustments",
      tags: "GROWTH"
    }, {
      name: "Operational Efficiency",
      notes: "Streamlining revenue management workflows",
      tags: "TECH"
    }]
  }];
  const complementaryServices = [{
    title: "Dynamic Pricing & Forecasting",
    href: "/DynamicPricingForecastingServices2Page"
  }, {
    title: "OTA Listing Optimization",
    href: "/OTAListingOptimizationServices2Page"
  }, {
    title: "Channel Manager Setup",
    href: "/ChannelManagerSetupDistributionStrategyPage"
  }, {
    title: "Tool Expertise & Tech Stack",
    href: "/ToolExpertiseTechnologyStackPage"
  }, {
    title: "Free Revenue Audit",
    href: "/FreeHotelRevenueAuditNoCostCompleteAnalysisPage"
  }];
  return <section data-section-id="1342" id="servicenavigation" className="relative w-full overflow-hidden bg-background py-32 md:py-48 font-default">
      <div ref={cover.ref} className={`mx-auto max-w-7xl px-6 md:px-16 text-center transition-all duration-700 ease-out ${cover.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="flex flex-col items-center gap-6">
          <p className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground">
            § HotelRevBaBa — Revenue Consultancy Services
          </p>
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px w-24 bg-primary" />
            <span className="h-1 w-1 bg-primary" />
          </div>
          <h1 className="font-default font-light text-foreground" style={{
          fontSize: "clamp(3.25rem, 8.4vw, 9.5rem)",
          letterSpacing: "-0.045em",
          lineHeight: 0.92,
          fontWeight: 200
        }}>
            Maximize your{" "}
            <span className="font-serif italic" style={{
            fontWeight: 300
          }}>
              revenue
            </span>
            .
          </h1>
          <p className="max-w-2xl text-muted-foreground" style={{
          fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
          letterSpacing: "-0.005em",
          lineHeight: 1.5,
          fontWeight: 300
        }}>
            Expert revenue management consulting led by Sunil Singh, designed to help hotel owners and investors unlock their property's full potential.
          </p>
        </div>
      </div>

      <div className="relative mt-32 md:mt-48 border-t border-b border-border overflow-hidden">
        <div className="flex whitespace-nowrap py-3 animate-[ticker_40s_linear_infinite]">
          {Array.from({
          length: 4
        }).map((_, i) => <span key={i} data-index={i} className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground px-6 shrink-0">
              — Revenue Management · OTA Strategy · Tech Implementation · Revenue Audits · HotelRevBaBa
            </span>)}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-9">
            {collections.map((c, i) => <CollectionBlock key={c.index} collection={c} i={i} isLast={i === collections.length - 1} />)}
          </div>
          
          <aside className="lg:col-span-3 pt-32 md:pt-48">
            <div className="sticky top-32">
              <p className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground mb-8">
                § Navigation — Services
              </p>
              <MenuProvider menu_id="42">
                <SidebarMenu />
              </MenuProvider>
            </div>
          </aside>
        </div>

        <div className="mt-32 pt-16 border-t border-border">
          <p className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground mb-10">
            § See also — Specialized Solutions
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {complementaryServices.map((service, idx) => <Link key={idx} data-index={idx} to={service.href} className="group flex flex-col gap-4 p-6 border border-border hover:border-primary transition-all duration-300">
                <span className="font-mono text-[0.6875rem] text-muted-foreground">0{idx + 1}</span>
                <h4 className="font-default font-light text-lg leading-tight group-hover:text-primary transition-colors">
                  {service.title}
                </h4>
                <ArrowRight className="size-4 text-primary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Link>)}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}
function SidebarMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-12 w-full bg-muted animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu orientation="vertical" className="max-w-none items-start justify-start">
      <NavigationMenuList className="flex-col items-start gap-2">
        {menuItems.map(item => <NavigationMenuItem key={item.id} className="w-full">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="flex items-center justify-between w-full py-3 px-4 border border-border hover:border-primary transition-colors group">
                <span className="font-mono text-[0.75rem] uppercase tracking-wider">{item.label}</span>
                <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
type Collection = {
  index: string;
  eyebrow: string;
  titleStart: string;
  titleItalic: string;
  titleEnd: string;
  description: string;
  image: string;
  imageAlt: string;
  ghostEdge: string;
  dishes: {
    name: string;
    notes: string;
    tags: string;
  }[];
};
function CollectionBlock({
  collection,
  i,
  isLast
}: {
  collection: Collection;
  i: number;
  isLast: boolean;
}) {
  const block = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isRightEdge = collection.ghostEdge === "right";
  return <div data-index={i} ref={block.ref} className={`relative pt-32 md:pt-48 ${isLast ? "" : "pb-0"} transition-all duration-700 ease-out ${block.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div aria-hidden="true" className={`pointer-events-none absolute top-24 ${isRightEdge ? "-right-12 md:-right-24" : "-left-12 md:-left-24"} select-none`} style={{
      opacity: 0.08
    }}>
        <span className="block font-default font-light text-foreground" style={{
        fontSize: "clamp(280px, 42vw, 620px)",
        lineHeight: 0.8,
        WebkitTextStroke: "1px currentColor",
        WebkitTextFillColor: "transparent",
        color: "var(--foreground)",
        fontWeight: 200
      }}>
          {collection.index}
        </span>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-5 md:col-start-8 md:order-2 order-1">
          <div className="relative">
            <PlateCoords />
            <div className="overflow-hidden">
              <img src={collection.image} data-wvc-srcset={`${collection.image}-wvc-srcset`} data-wvc-sizes={`${collection.image}-wvc-sizes`} alt={collection.imageAlt} loading="lazy" decoding="async" className="w-full aspect-[3/5] object-cover transition-transform duration-[600ms] ease-out hover:scale-[1.03]" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground">
                Service · 0{i + 1}
              </span>
              <span className="font-mono uppercase text-[0.6875rem] tracking-[0.12em] text-muted-foreground">
                Consulting
              </span>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 md:order-1 order-2">
          <p className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground">
            {collection.eyebrow}
          </p>

          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <span className="h-px w-[72px] bg-primary" />
            <span className="h-1 w-1 bg-primary" />
          </div>

          <h2 className="mt-8 font-default font-light text-foreground max-w-3xl" style={{
          fontSize: "clamp(2.25rem, 5.2vw, 5.5rem)",
          letterSpacing: "-0.035em",
          lineHeight: 0.98,
          fontWeight: 300
        }}>
            {collection.titleStart}{" "}
            <span className="font-serif italic" style={{
            fontWeight: 300
          }}>
              {collection.titleItalic}
            </span>
            {collection.titleEnd ? ` ${collection.titleEnd}` : ""}
          </h2>

          <p className="mt-8 max-w-2xl text-muted-foreground" style={{
          fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
          letterSpacing: "-0.005em",
          lineHeight: 1.5,
          fontWeight: 300
        }}>
            {collection.description}
          </p>

          <ul className="mt-14 border-t border-border">
            {collection.dishes.map((d, di) => <li data-index={di} key={di} className="group relative grid grid-cols-12 gap-4 py-6 border-b border-border transition-colors duration-300 hover:border-primary">
                <div className="col-span-12 md:col-span-5 flex items-baseline gap-4">
                  <span className="font-mono uppercase text-[0.6875rem] tracking-[0.12em] text-muted-foreground shrink-0 w-8">
                    {String(di + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-default font-light text-foreground" style={{
                fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
                fontWeight: 300
              }}>
                    {d.name}
                  </h3>
                </div>
                <div className="col-span-10 md:col-span-5 self-center">
                  <p className="font-mono text-[0.75rem] tracking-[0.12em] text-muted-foreground uppercase">
                    {d.notes}
                  </p>
                </div>
                <div className="col-span-2 self-center flex items-center justify-end gap-3">
                  {d.tags ? <span className="font-mono uppercase text-[0.6875rem] tracking-[0.22em] text-muted-foreground border border-border px-2 py-0.5">
                      {d.tags}
                    </span> : null}
                  <ArrowRight className="size-4 text-primary opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1" aria-hidden="true" />
                </div>
              </li>)}
          </ul>
        </div>
      </div>

      {!isLast && <div className="relative mt-24 md:mt-32 border-t border-b border-border overflow-hidden">
          <div className="flex whitespace-nowrap py-3 animate-[ticker_40s_linear_infinite]">
            {Array.from({
          length: 4
        }).map((_, ti) => <span key={ti} data-index={ti} className="font-mono uppercase text-[0.75rem] tracking-[0.22em] text-muted-foreground px-6 shrink-0">
                — Revenue Management · OTA Strategy · Tech Implementation · Revenue Audits · HotelRevBaBa
              </span>)}
          </div>
        </div>}
    </div>;
}
function PlateCoords() {
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10">
      <span className="absolute -top-1 -left-1 text-muted-foreground font-mono text-[10px] leading-none">+</span>
      <span className="absolute -top-1 -right-1 text-muted-foreground font-mono text-[10px] leading-none">+</span>
      <span className="absolute -bottom-1 -left-1 text-muted-foreground font-mono text-[10px] leading-none">+</span>
      <span className="absolute -bottom-1 -right-1 text-muted-foreground font-mono text-[10px] leading-none">+</span>
    </div>;
}